import { HubSpotProductName } from "src/hooks/useRevenueHero";
import { isProd } from "../../../env";

interface StripeProductIds {
  [productName: string]: {
    dev: string;
    prod: string;
  };
}

const STRIPE_PRODUCT_IDS = {
  core: {
    dev: "prod_SOZ6bQXuuiy454",
    prod: "prod_Fd1KwjnPcMo5pp",
  },
  train: {
    dev: "prod_SOZh8ILxeOdkBF",
    prod: "prod_KKOvS6FdM3QbxJ",
  },
  brandedApp: {
    dev: "prod_SOZLeWisWXlcts",
    prod: "prod_HXlUJRqaNMuXq3",
  },
  cpBrandedApp: {
    dev: "prod_SSzrtNhIhMxP8i",
    prod: "prod_R4vfthqYMSIAN5",
  },
  grow2021: {
    dev: "prod_SOZHViqcAKu0RM",
    prod: "prod_IbQlAmw02b08ig",
  },
  growArchived: {
    dev: "prod_SOzD8ZHp394pyI",
    prod: "prod_JyABX5rF7GJWeV",
  },
  growLegacy: {
    dev: "prod_SOzHsh1NBcLdAY",
    prod: "prod_JyABRCjmUWgKh5",
  },
  grow_4_1_24: {
    dev: "prod_SOzCUrQRdEnqw8",
    prod: "prod_PpPFruGeM0R67f",
  },
} as const satisfies StripeProductIds;

type ValidProductName = keyof typeof STRIPE_PRODUCT_IDS;

type ValidProductId = {
  [K in keyof typeof STRIPE_PRODUCT_IDS]:
    | (typeof STRIPE_PRODUCT_IDS)[K]["dev"]
    | (typeof STRIPE_PRODUCT_IDS)[K]["prod"];
}[keyof typeof STRIPE_PRODUCT_IDS];

function getProductId<T extends ValidProductName>(
  product: T,
  environment: "dev" | "prod",
) {
  return STRIPE_PRODUCT_IDS[product][environment];
}

interface ProductInfo {
  priceId: string;
  productId: ValidProductId;
}
interface PriceId {
  productTag: string;
  product: HubSpotProductName;
  dev: ProductInfo;
  prod: ProductInfo;
}

const CORE_STRIPE_PRODUCT_PRICES = [
  {
    productTag: "core-free",
    product: "Core",
    dev: {
      priceId: "price_1RTlyWLSJXl9aXnwEdVbSyoM",
      productId: getProductId("core", "dev"),
    },
    prod: {
      priceId: "pp-prod-core-free",
      productId: getProductId("core", "prod"),
    },
  },
  {
    productTag: "core-pro-monthly",
    product: "Core",
    dev: {
      priceId: "price_1RTm77LSJXl9aXnwncQ24FmX",
      productId: getProductId("core", "dev"),
    },
    prod: {
      priceId: "pp-prod-core-pro-monthly-159",
      productId: getProductId("core", "prod"),
    },
  },
  {
    productTag: "core-pro-annual",
    product: "Core",
    dev: {
      priceId: "price_1RTm7ZLSJXl9aXnw1wCNylSm",
      productId: getProductId("core", "dev"),
    },
    prod: {
      priceId: "pp-prod-core-pro-annual-1599",
      productId: getProductId("core", "prod"),
    },
  },
  {
    productTag: "core-max-monthly",
    product: "Core",
    dev: {
      priceId: "price_1RTm7zLSJXl9aXnw5slUP9Ca",
      productId: getProductId("core", "dev"),
    },
    prod: {
      priceId: "pp-prod-core-max-monthly-229",
      productId: getProductId("core", "prod"),
    },
  },
  {
    productTag: "core-max-annual",
    product: "Core",
    dev: {
      priceId: "price_1RTm8PLSJXl9aXnwXmYxNox3",
      productId: getProductId("core", "dev"),
    },
    prod: {
      priceId: "pp-prod-core-max-annual-2299",
      productId: getProductId("core", "prod"),
    },
  },
  {
    productTag: "core-annual-discounted",
    product: "Core",
    dev: {
      priceId: "price_1RYy3LLSJXl9aXnw2dIEyg8I",
      productId: getProductId("core", "dev"),
    },
    prod: {
      priceId: "price_1RVhKRLSJXl9aXnwjQg2CEce",
      productId: getProductId("core", "prod"),
    },
  },
] as const satisfies PriceId[];

const TRAIN_STRIPE_PRODUCT_PRICES = [
  {
    productTag: "train-free",
    product: "Train",
    dev: {
      priceId: "price_1RTmYLLSJXl9aXnwEhEDSqjj",
      productId: getProductId("train", "dev"),
    },
    prod: {
      priceId: "Train-Monthly-Free1",
      productId: getProductId("train", "prod"),
    },
  },
  {
    productTag: "train-tier-one",
    product: "Train",
    dev: {
      priceId: "price_1RTmZXLSJXl9aXnwdP1H0H8S",
      productId: getProductId("train", "dev"),
    },
    prod: {
      priceId: "Train-Monthly-Metered1",
      productId: getProductId("train", "prod"),
    },
  },
  {
    productTag: "train-tier-two",
    product: "Train",
    dev: {
      priceId: "price_1RTmZ9LSJXl9aXnwXm2EQDJY",
      productId: getProductId("train", "dev"),
    },
    prod: {
      priceId: "price_1Q7k8hLSJXl9aXnwTjhLzFKX",
      productId: getProductId("train", "prod"),
    },
  },
  {
    productTag: "train-annual-discounted",
    product: "Train",
    dev: {
      priceId: "price_1RYy9MLSJXl9aXnwxvg061Yc",
      productId: getProductId("train", "dev"),
    },
    prod: {
      priceId: "price_1RVhI6LSJXl9aXnwYYy0FjUh",
      productId: getProductId("train", "prod"),
    },
  },
] as const satisfies PriceId[];

const BRANDED_APP_STRIPE_PRODUCT_PRICES = [
  {
    productTag: "branded-app",
    product: "Branded App",
    dev: {
      priceId: "price_1RTmCsLSJXl9aXnwwk0NFMOu",
      productId: getProductId("brandedApp", "dev"),
    },
    prod: {
      priceId: "pp-prod-addon-branded-app",
      productId: getProductId("brandedApp", "prod"),
    },
  },
  {
    productTag: "cp-branded-app",
    product: "Branded App",
    dev: {
      priceId: "price_1RY3rNLSJXl9aXnwfSiovnb2",
      productId: getProductId("cpBrandedApp", "dev"),
    },
    prod: {
      priceId: "price_1QCloOLSJXl9aXnwoklXSbfA",
      productId: getProductId("cpBrandedApp", "prod"),
    },
  },
  {
    productTag: "branded-app-annual-discounted",
    product: "Branded App",
    dev: {
      priceId: "price_1RYy7BLSJXl9aXnwOwv959a5",
      productId: getProductId("brandedApp", "dev"),
    },
    prod: {
      priceId: "price_1RVhJ2LSJXl9aXnwqgZZS05i",
      productId: getProductId("brandedApp", "prod"),
    },
  },
] as const satisfies PriceId[];

// Due to historical reasons, Grow has several variants. In Stripe
// these are represented as different price IDs under various products,
// but to our customers it is all the same Grow product.
const GROW_STRIPE_PRODUCT_PRICES = [
  {
    productTag: "grow-monthly-4-1-24",
    product: "Grow",
    dev: {
      priceId: "price_1RUBE8LSJXl9aXnwjE4lKiZK",
      productId: getProductId("grow_4_1_24", "dev"),
    },
    prod: {
      priceId: "price_1OzkQXLSJXl9aXnwE8OU6Bk5",
      productId: getProductId("grow_4_1_24", "prod"),
    },
  },
  {
    productTag: "grow-archived-full-price",
    product: "Grow",
    dev: {
      priceId: "price_1RUBEuLSJXl9aXnwAsIr7Lse",
      productId: getProductId("growArchived", "dev"),
    },
    prod: {
      priceId: "price_1JZiuJLSJXl9aXnw2g8wuDHE",
      productId: getProductId("growArchived", "prod"),
    },
  },
  {
    productTag: "grow-archived-discounted",
    product: "Grow",
    dev: {
      priceId: "price_1RUBEiLSJXl9aXnwviSOJmx7",
      productId: getProductId("growArchived", "dev"),
    },
    prod: {
      priceId: "price_1JQdbPLSJXl9aXnwxpfxpq3i",
      productId: getProductId("growArchived", "prod"),
    },
  },
  {
    productTag: "grow-archived-free",
    product: "Grow",
    dev: {
      priceId: "price_1RUBF1LSJXl9aXnwPa7w2avc",
      productId: getProductId("growArchived", "dev"),
    },
    prod: {
      priceId: "price_1JLeyELSJXl9aXnwGeez1DUi",
      productId: getProductId("growArchived", "prod"),
    },
  },
  {
    productTag: "grow-legacy-free",
    product: "Grow",
    dev: {
      priceId: "price_1RUBIXLSJXl9aXnwb02Vqe9U",
      productId: getProductId("growLegacy", "dev"),
    },
    prod: {
      priceId: "price_1JQds3LSJXl9aXnwMcylmKGL",
      productId: getProductId("growLegacy", "prod"),
    },
  },
  {
    productTag: "grow-2021-unified",
    product: "Grow",
    dev: {
      priceId: "price_1RTm9LLSJXl9aXnwfY4i5nya",
      productId: getProductId("grow2021", "dev"),
    },
    prod: {
      priceId: "pp-grow-unified-monthly-299",
      productId: getProductId("grow2021", "prod"),
    },
  },
  {
    productTag: "grow-2021-monthly",
    product: "Grow",
    dev: {
      priceId: "price_1RTmB0LSJXl9aXnwug66fVo6",
      productId: getProductId("grow2021", "dev"),
    },
    prod: {
      priceId: "pushpress-grow",
      productId: getProductId("grow2021", "prod"),
    },
  },
  {
    productTag: "grow-annual-discounted",
    product: "Grow",
    dev: {
      priceId: "price_1RYy5NLSJXl9aXnwI1Td7rVt",
      productId: getProductId("grow_4_1_24", "dev"),
    },
    prod: {
      priceId: "price_1RVhJcLSJXl9aXnwsNv6BUlo",
      productId: getProductId("grow_4_1_24", "prod"),
    },
  },
] as const satisfies PriceId[];

const STRIPE_PRODUCT_PRICES = [
  ...CORE_STRIPE_PRODUCT_PRICES,
  ...TRAIN_STRIPE_PRODUCT_PRICES,
  ...BRANDED_APP_STRIPE_PRODUCT_PRICES,
  ...GROW_STRIPE_PRODUCT_PRICES,
] as const satisfies PriceId[];

type ValidProductTag = (typeof STRIPE_PRODUCT_PRICES)[number]["productTag"];

function getPriceIdForCurrentEnvironment(productTag: ValidProductTag) {
  const environment = isProd() ? "prod" : "dev";
  const priceInfo = STRIPE_PRODUCT_PRICES.find(
    (price) => price.productTag === productTag,
  );
  return priceInfo[environment].priceId;
}

export const STRIPE_PRICE_IDS = {
  coreFree: getPriceIdForCurrentEnvironment("core-free"),
  coreProMonthly: getPriceIdForCurrentEnvironment("core-pro-monthly"),
  coreProAnnual: getPriceIdForCurrentEnvironment("core-pro-annual"),
  coreMaxMonthly: getPriceIdForCurrentEnvironment("core-max-monthly"),
  coreMaxAnnual: getPriceIdForCurrentEnvironment("core-max-annual"),
  coreAnnualDiscounted: getPriceIdForCurrentEnvironment(
    "core-annual-discounted",
  ),
  trainFree: getPriceIdForCurrentEnvironment("train-free"),
  trainTierOne: getPriceIdForCurrentEnvironment("train-tier-one"),
  trainTierTwo: getPriceIdForCurrentEnvironment("train-tier-two"),
  trainAnnualDiscounted: getPriceIdForCurrentEnvironment(
    "train-annual-discounted",
  ),
  growMonthly_4_1_24: getPriceIdForCurrentEnvironment("grow-monthly-4-1-24"),
  growArchivedFullPrice: getPriceIdForCurrentEnvironment(
    "grow-archived-full-price",
  ),
  growArchivedDiscounted: getPriceIdForCurrentEnvironment(
    "grow-archived-discounted",
  ),
  growArchivedFree: getPriceIdForCurrentEnvironment("grow-archived-free"),
  growLegacyFree: getPriceIdForCurrentEnvironment("grow-legacy-free"),
  grow2021Unified: getPriceIdForCurrentEnvironment("grow-2021-unified"),
  grow2021Monthly: getPriceIdForCurrentEnvironment("grow-2021-monthly"),
  growAnnualDiscounted: getPriceIdForCurrentEnvironment(
    "grow-annual-discounted",
  ),
  brandedApp: getPriceIdForCurrentEnvironment("branded-app"),
  cpBrandedApp: getPriceIdForCurrentEnvironment("cp-branded-app"),
  brandedAppAnnualDiscounted: getPriceIdForCurrentEnvironment(
    "branded-app-annual-discounted",
  ),
};

export const CORE_STRIPE_PRICE_IDS: string[] = [
  STRIPE_PRICE_IDS.coreFree,
  STRIPE_PRICE_IDS.coreProMonthly,
  STRIPE_PRICE_IDS.coreProAnnual,
  STRIPE_PRICE_IDS.coreMaxMonthly,
  STRIPE_PRICE_IDS.coreMaxAnnual,
  STRIPE_PRICE_IDS.coreAnnualDiscounted,
];

export const GROW_STRIPE_PRICE_IDS: string[] = [
  STRIPE_PRICE_IDS.growMonthly_4_1_24,
  STRIPE_PRICE_IDS.growArchivedFullPrice,
  STRIPE_PRICE_IDS.growArchivedDiscounted,
  STRIPE_PRICE_IDS.growArchivedFree,
  STRIPE_PRICE_IDS.growLegacyFree,
  STRIPE_PRICE_IDS.grow2021Unified,
  STRIPE_PRICE_IDS.grow2021Monthly,
  STRIPE_PRICE_IDS.growAnnualDiscounted,
];

// Excludes Train Free
export const TRAIN_STRIPE_PRICE_IDS: string[] = [
  STRIPE_PRICE_IDS.trainTierOne,
  STRIPE_PRICE_IDS.trainTierTwo,
  STRIPE_PRICE_IDS.trainAnnualDiscounted,
];

export const BRANDED_APP_STRIPE_PRICE_IDS: string[] = [
  STRIPE_PRICE_IDS.brandedApp,
  STRIPE_PRICE_IDS.cpBrandedApp,
  STRIPE_PRICE_IDS.brandedAppAnnualDiscounted,
];
